import fc from "@/firebaseConfig";
import { getDefaultArDateRange } from "@/libs/arDateRange";

const defaultDateRange = getDefaultArDateRange();

export default {
  namespaced: true,
  
  state: {
    fileTracks: [],
    // Last snapshot as it arrived, before the client-side filename filter.
    // Kept so the filter can be re-applied without re-subscribing.
    rawFileTracks: [],
    // Card totals come from apiPurchaseFileTrackStatusCount. They used to be
    // counted client-side from an unfiltered listener over the whole
    // collection — 6,000+ documents on a busy company, downloaded on every
    // visit and growing forever, to render three numbers.
    statusCountTotals: { PROCESSING: 0, SUCCESS: 0, ERROR: 0, total: 0 },
    displayedItems: [],
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    loading: false,
    unsubscribe: null,
    // What the live listeners are currently bound to. Re-subscribing costs a
    // full re-download of the result set, so each is rebuilt only when the
    // thing it is bound to actually changes.
    allTracksCompanyId: null,
    querySignature: null,
    
    // Search/Filter
    searchFileName: "",
    searchStatus: "SHOW_ALL",
    searchStartDate: defaultDateRange.fromDate,
    searchEndDate: defaultDateRange.toDate,
    
    // Sorting
    sortBy: "timeUpdate",
    sortDirection: "desc",
  },
  
  mutations: {
    SET_FILE_TRACKS(state, tracks) {
      state.fileTracks = tracks;
      state.totalItems = tracks.length;
    },
    
    SET_RAW_FILE_TRACKS(state, tracks) {
      state.rawFileTracks = tracks;
    },
    
    SET_STATUS_COUNT_TOTALS(state, counts) {
      state.statusCountTotals = counts;
    },
    
    SET_DISPLAYED_ITEMS(state, items) {
      state.displayedItems = items;
    },
    
    SET_CURRENT_PAGE(state, page) {
      state.currentPage = page;
    },
    
    SET_ITEMS_PER_PAGE(state, count) {
      state.itemsPerPage = count;
    },
    
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    
    SET_UNSUBSCRIBE(state, unsubscribe) {
      state.unsubscribe = unsubscribe;
    },
    
    SET_ALL_TRACKS_COMPANY(state, companyId) {
      state.allTracksCompanyId = companyId;
    },
    
    SET_QUERY_SIGNATURE(state, signature) {
      state.querySignature = signature;
    },
    
    CLEAR_UNSUBSCRIBE(state) {
      if (state.unsubscribe) {
        state.unsubscribe();
      }
      state.unsubscribe = null;
      state.allTracksCompanyId = null;
      state.querySignature = null;
      state.rawFileTracks = [];
    },
    
    SET_SEARCH_FILENAME(state, filename) {
      state.searchFileName = filename;
    },
    
    SET_SEARCH_STATUS(state, status) {
      state.searchStatus = status;
    },
    
    SET_SEARCH_DATES(state, { startDate, endDate }) {
      state.searchStartDate = startDate;
      state.searchEndDate = endDate;
    },
    
    SET_SORT(state, { sortBy, sortDirection }) {
      state.sortBy = sortBy;
      state.sortDirection = sortDirection;
    },
    
    RESET_SEARCH(state) {
      const resetRange = getDefaultArDateRange();
      state.searchFileName = "";
      state.searchStatus = "SHOW_ALL";
      // Back to the default window, not to null: an empty filter subscribes to
      // the whole PurchaseFileTracks collection.
      state.searchStartDate = resetRange.fromDate;
      state.searchEndDate = resetRange.toDate;
    },
  },
  
  actions: {
    // Fetch file tracks with real-time updates
    async subscribeToFileTracks({ commit, state, dispatch }, { companyId }) {
      try {
        commit('SET_LOADING', true);
        
        // Card totals: counted server-side, not by listening to the whole
        // collection. Fire-and-forget so the table below is never held up by
        // it; the numbers land a moment later.
        if (state.allTracksCompanyId !== companyId) {
          commit('SET_ALL_TRACKS_COMPANY', companyId);
          dispatch('fetchStatusCounts', { companyId });
        }
        
        const { searchStatus, searchStartDate, searchEndDate } = state;
        const defaultStatusList = ["PROCESSING", "SUCCESS", "ERROR"];
        
        let query = fc.db.collection(`Companies/${companyId}/PurchaseFileTracks`);
        
        // Filter by status
        const statusList =
          !searchStatus || searchStatus === "SHOW_ALL"
            ? defaultStatusList
            : [searchStatus];
        query = query.where("status", "in", statusList);
        
        // Filter by date range
        if (searchStartDate) {
          const start = new Date(searchStartDate);
          start.setHours(0);
          
          const end = searchEndDate
            ? new Date(searchEndDate)
            : new Date(searchStartDate);
          end.setHours(23, 59, 59, 999);
          
          query = query.where("timeUpdate", ">=", start);
          query = query.where("timeUpdate", "<=", end);
        }
        
        query = query.orderBy("timeUpdate", "desc");
        
        // The filename box filters client-side, so it must stay out of the
        // signature — otherwise typing in it re-subscribes on every keystroke.
        const querySignature = JSON.stringify([
          companyId,
          statusList,
          searchStartDate || "",
          searchEndDate || "",
        ]);
        if (state.unsubscribe && state.querySignature === querySignature) {
          // Same query, live listener already attached. Only the client-side
          // filename filter can have changed, so re-apply it over the last
          // snapshot rather than pulling the whole result set again.
          dispatch('applyFilenameFilter');
          commit('SET_LOADING', false);
          return state.fileTracks;
        }
        if (state.unsubscribe) {
          state.unsubscribe();
        }
        commit('SET_QUERY_SIGNATURE', querySignature);
        
        // Setup real-time listener for filtered data
        const unsubscribe = query.onSnapshot(async (snapshot) => {
          const tracks = snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }));
          
          // No per-row PurchasePdfFiles read here on purpose. It used to fetch
          // one document per track to attach `refreshCount` — an extra read for
          // every row in the window, 800+ on a busy company — and nothing in
          // fileTracking.vue ever displayed the field. The screen only writes
          // it (FieldValue.increment on refresh).
          
          commit('SET_RAW_FILE_TRACKS', tracks);
          dispatch('applyFilenameFilter');
          commit('SET_LOADING', false);
        });
        
        commit('SET_UNSUBSCRIBE', unsubscribe);
      } catch (error) {
        console.error("Error subscribing to file tracks:", error);
        commit('SET_LOADING', false);
        throw error;
      }
    },
    
    // Card totals for the whole collection, counted by
    // apiPurchaseFileTrackStatusCount. Failure leaves the previous numbers in
    // place rather than blanking the cards.
    async fetchStatusCounts({ commit }, { companyId }) {
      try {
        const user = fc.auth.currentUser;
        if (!user) return;
        const token = await user.getIdToken();
        const response = await fetch(fc.getFunctionUrl("apiPurchaseFileTrackStatusCount"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ companyId }),
        });
        const data = await response.json();
        if (data && data.success && data.counts) {
          commit('SET_STATUS_COUNT_TOTALS', data.counts);
        }
      } catch (error) {
        console.error("Error fetching file-track status counts:", error);
      }
    },
    
    // Client-side filename filter over the last snapshot. Kept apart from the
    // subscription so typing in the box never re-queries Firestore.
    applyFilenameFilter({ commit, state, dispatch }) {
      let filteredTracks = state.rawFileTracks;
      if (state.searchFileName) {
        const needle = state.searchFileName.toLowerCase();
        filteredTracks = state.rawFileTracks.filter((track) => {
          const pdfPath = track.pdfPath || "";
          return pdfPath.toLowerCase().includes(needle);
        });
      }
      commit('SET_FILE_TRACKS', filteredTracks);
      commit('SET_CURRENT_PAGE', 1);
      dispatch('paginate');
    },
    
    // Unsubscribe from real-time updates
    unsubscribeFromFileTracks({ commit }) {
      commit('CLEAR_UNSUBSCRIBE');
    },
    
    // Paginate data
    paginate({ commit, state }) {
      const { currentPage, itemsPerPage, fileTracks } = state;
      const totalPages = Math.ceil(fileTracks.length / itemsPerPage);
      
      // Adjust current page if out of bounds
      const validPage = currentPage > totalPages ? totalPages || 1 : currentPage;
      if (validPage !== currentPage) {
        commit('SET_CURRENT_PAGE', validPage);
      }
      
      const startIndex = (validPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const items = fileTracks.slice(startIndex, endIndex);
      
      commit('SET_DISPLAYED_ITEMS', items);
    },
    
    // Sort table
    sortTable({ commit, state, dispatch }, column) {
      let sortDirection = state.sortDirection;
      
      if (state.sortBy === column) {
        sortDirection = sortDirection === "asc" ? "desc" : "asc";
      } else {
        sortDirection = "asc";
      }
      
      commit('SET_SORT', { sortBy: column, sortDirection });
      
      // Sort the tracks
      const sortedTracks = [...state.fileTracks].sort((a, b) => {
        let result = 0;
        if (a[column] < b[column]) result = -1;
        if (a[column] > b[column]) result = 1;
        return sortDirection === "asc" ? result : -result;
      });
      
      commit('SET_FILE_TRACKS', sortedTracks);
      dispatch('paginate');
    },
    
    // Update search filters
    updateSearch({ commit, dispatch }, { companyId, searchFileName, searchStatus, searchStartDate, searchEndDate }) {
      if (searchFileName !== undefined) {
        commit('SET_SEARCH_FILENAME', searchFileName);
      }
      if (searchStatus !== undefined) {
        commit('SET_SEARCH_STATUS', searchStatus);
      }
      if (searchStartDate !== undefined || searchEndDate !== undefined) {
        commit('SET_SEARCH_DATES', { startDate: searchStartDate, endDate: searchEndDate });
      }
      
      // Re-fetch with new filters
      if (companyId) {
        dispatch('subscribeToFileTracks', { companyId });
      }
    },
    
    // Reset search
    resetSearch({ commit, dispatch }, { companyId }) {
      commit('RESET_SEARCH');
      dispatch('subscribeToFileTracks', { companyId });
    },
    
    // Set current page
    setCurrentPage({ commit, dispatch }, page) {
      commit('SET_CURRENT_PAGE', page);
      dispatch('paginate');
    },
    
    // Set items per page
    setItemsPerPage({ commit, dispatch }, count) {
      commit('SET_ITEMS_PER_PAGE', count);
      dispatch('paginate');
    },
  },
  
  getters: {
    fileTracks: (state) => state.fileTracks,
    displayedItems: (state) => state.displayedItems,
    currentPage: (state) => state.currentPage,
    itemsPerPage: (state) => state.itemsPerPage,
    totalItems: (state) => state.totalItems,
    loading: (state) => state.loading,
    searchFileName: (state) => state.searchFileName,
    searchStatus: (state) => state.searchStatus,
    searchStartDate: (state) => state.searchStartDate,
    searchEndDate: (state) => state.searchEndDate,
    sortBy: (state) => state.sortBy,
    sortDirection: (state) => state.sortDirection,
    
    numPages: (state) => {
      return Math.ceil(state.totalItems / state.itemsPerPage);
    },
    
    itemsRangeText: (state) => {
      const start = (state.currentPage - 1) * state.itemsPerPage + 1;
      const end = Math.min(
        state.currentPage * state.itemsPerPage,
        state.totalItems
      );
      return `${start} - ${end} จาก ${state.totalItems}`;
    },
    
    statusCounts: (state) => state.statusCountTotals,
  },
};

